import { useCallback, useState } from 'react';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';

import { Callback } from 'screens/TelehealthScreen/utils/twilioCustomTypes';
import { RoomEvents } from 'screens/TelehealthScreen/utils/enums';
import { saveTelehealthLog } from 'screens/TelehealthScreen/utils/helpers';
import { useLiveRef } from 'hooks';

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const optionsRef = useLiveRef(options);

  const connect = useCallback(
    (token) => {
      setIsConnecting(true);
      return Video.connect(token, { ...optionsRef.current, tracks: localTracks }).then(
        (newRoom) => {
          setRoom(newRoom);

          // This app can add up to 13 'participantDisconnected' listeners to the room object, which can trigger
          // a warning from the EventEmitter object. Here we increase the max listeners to suppress the warning.
          newRoom.setMaxListeners(100);

          newRoom.once(RoomEvents.Disconnected, () => {
            saveTelehealthLog('Patient/Guest - Room - disconnected from the call');
            // Reset the room only after all other `disconnected` listeners have been called.
            setTimeout(() => setRoom(null));
          });

          newRoom.localParticipant.videoTracks.forEach((publication) =>
            // All video tracks are published with 'low' priority because the video track
            // that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            // @ts-ignore
            publication.setPriority('low'),
          );

          (window as any).twilioRoom = newRoom;

          saveTelehealthLog('Patient/Guest - Room - connected to the call', newRoom);
          setIsConnecting(false);
        },
        (error) => {
          onError(error, 'Room - Error on connect method');
          setIsConnecting(false);
        },
      );
    },
    [localTracks, onError, optionsRef],
  );

  return { room, isConnecting, connect };
}
